import {
  ModelForm,
  ModelSelect,
  Flex,
  FlexColumn,
  flattenErrorTypeArray,
  ModelDateInline,
  ModelNewNumericInput,
  Errors,
  ValidIcon,
  InvalidIcon,
  SectionIconButton,
  TextFieldInput,
  ModelTripleToggle,
  TextInput,
  ErrorTypes,
  WarningIcon,
  useStyledTheme,
  useDeleteAlert,
  useCreateAndAddRefs,
} from '@equifinance/shared';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateFormatStringEnum } from '@equifinance/shared/utils';
import merge from 'lodash/merge';
import moment from 'moment';
import {
  Insolvency, ConsolidationType,
  useCreditContext,
  TypeOfJudgementType,
  checkInsolvencyIsSettled,
  CreditAddress,
} from '../../contexts/credit';
import InsolvencyDetails from './CreditDetails/InsolvencyDetails/InsolvencyDetails';
import CreditTableAddress from './CreditComponents/CreditTableAddress';
import CreditTable, { creditLineErrorTypes, creditLineWarningTypes } from './CreditTable';
import {
  DeleteButton,
  IgnoreButton,
  IgnoredItem,
  IgnoredTag,
  SettledItem,
} from './CreditTableItem';
import { ApplicantName } from './CreditTables';

const CreditOptionsContainer = styled(Flex)` ${({ theme }) => `
    gap:${theme.padding[4]};
  `}`;

const StatusIconContainer = styled.div` ${({ theme }) => `
  width: ${theme.padding[6]};
  display: flex;
`}`;

const TableContainer = styled(FlexColumn)` ${({ theme }) => `
  padding-top: ${theme.padding[4]};
  gap: ${theme.padding[1]};
  
  ${SectionIconButton} {
    :not([disabled]) {
      color: ${theme.colours.primary[1]};
      :hover {
        transform: none;
      }
    }
  }
  
  ${TextFieldInput} {
    &:disabled::placeholder {
      color:  ${theme.colours.neutral[2]};
    }
  }
`}`;

function StatusIcon(insolvency: Insolvency) {
  const theme = useStyledTheme();
  const { id, ignored } = insolvency;
  const { credit: { errors, warnings } } = useCreditContext();
  const isSettled = checkInsolvencyIsSettled(insolvency);
  // eslint-disable-next-line react/destructuring-assignment
  const itemErrors = flattenErrorTypeArray(errors?.[id] || []);
  const hasValidationFailures = itemErrors.some((error) => error.errorType === ErrorTypes.ValidationFailure);
  const completed = !hasValidationFailures;
  const isHidden = ignored || isSettled;

  if (isHidden) {
    return null;
  }

  const itemErrorsAndWarnings = flattenErrorTypeArray(
    merge({}, errors, warnings)[id]
      ?.filter((e) => e.propertyName == null),
  );

  const isItemError = itemErrorsAndWarnings.find((e) => creditLineErrorTypes.includes(e.errorType));
  const isItemWarning = itemErrorsAndWarnings.find((e) => creditLineWarningTypes.includes(e.errorType));

  if (isItemError || isItemWarning) {
    return (
      <StatusIconContainer>
        <WarningIcon
          stroke={isItemError ? theme.colours.danger[1] : theme.colours.secondary[1]}
          fill={theme.colours.neutral[10]}
        />
      </StatusIconContainer>
    );
  }

  return completed ? <ValidIcon /> : <InvalidIcon />;
}

function InsolvencyCourtDate(
  insolvency: Insolvency,
  baseUpdate: (insolvency: Insolvency, values: any, disableIncrementUpdateCount?: boolean) => void,
  errors: Errors,
  warnings: Errors,
) {
  const {
    id,
    isEquifax,
    ignored,
    hasCourtDateFromEquifax,
  } = insolvency;

  const disabled = isEquifax && (ignored || checkInsolvencyIsSettled(insolvency) || hasCourtDateFromEquifax);

  const [showOverrideError, setShowOverrideError] = useState<boolean>(false);

  const updateFunc = (values: any, disableIncrementUpdateCount: boolean = false) => {
    if (!values.courtDate) {
      setShowOverrideError(false);
      baseUpdate(insolvency, { courtDate: null }, disableIncrementUpdateCount);
      return;
    }

    const date = moment(values.courtDate, DateFormatStringEnum.ISO8601, true);
    const isValid = date.isValid() && date.year() > 1900;
    setShowOverrideError(!isValid);
    if (isValid) {
      baseUpdate(insolvency, values, disableIncrementUpdateCount);
    }
  };

  return (
    <ModelForm model={insolvency} update={updateFunc} errors={errors} warnings={warnings}>
      <ModelDateInline
        id={id}
        disabled={disabled}
        field="courtDate"
        placeholder={disabled ? '-' : 'DD/MM/YYYY'}
        errorOverride={showOverrideError ? 'Date is invalid' : ''}
      />
    </ModelForm>
  );
}

function InsolvencySatisfiedDate(
  insolvency: Insolvency,
  baseUpdate: (insolvency: Insolvency, values: any, disableIncrementUpdateCount?: boolean) => void,
  errors: Errors,
  warnings: Errors,
) {
  const { id, isEquifax } = insolvency;

  const [showOverrideError, setShowOverrideError] = useState<boolean>(false);

  const updateFunc = (values: any, disableIncrementUpdateCount: boolean = false) => {
    if (!values.satisfiedDate) {
      setShowOverrideError(false);
      baseUpdate(insolvency, { satisfiedDate: null }, disableIncrementUpdateCount);
      return;
    }

    const date = moment(values.satisfiedDate, DateFormatStringEnum.ISO8601, true);
    const isValid = date.isValid() && date.year() > 1900;
    setShowOverrideError(!isValid);
    if (isValid) {
      baseUpdate(insolvency, values, disableIncrementUpdateCount);
    }
  };

  if (isEquifax && !checkInsolvencyIsSettled(insolvency)) {
    return (
      <TextInput
        id={`${id}-live`}
        value="LIVE"
        onChange={() => { }}
        disabled
      />
    );
  }

  return (
    <ModelForm model={insolvency} update={updateFunc} errors={errors} warnings={warnings}>
      <ModelDateInline
        id={id}
        disabled={isEquifax}
        field="satisfiedDate"
        placeholder="DD/MM/YYYY"
        errorOverride={showOverrideError ? 'Date is invalid' : ''}
      />
    </ModelForm>
  );
}

function InsolvencyOptions(
  insolvency: Insolvency,
  deleteItems: (insolvencyItem: Insolvency) => void,
  update: (values: any, disableIncrementUpdateCount?: boolean) => Promise<void>,
) {
  const {
    id,
    isEquifax,
    typeOfJudgement,
  } = insolvency;

  const deleteAlert = useDeleteAlert();

  const onDelete = () => {
    deleteAlert({
      onConfirm: () => deleteItems(insolvency),
      text: 'Are you sure you want to delete this credit line?',
    });
  };

  return (
    <CreditOptionsContainer>
      <IgnoreButton data={insolvency} update={update} accountType={typeOfJudgement} />
      {!isEquifax && <DeleteButton onDelete={onDelete} id={id} />}
    </CreditOptionsContainer>
  );
}

export default function InsolvencyTable({ address }: { address: CreditAddress }) {
  const {
    credit,
    addInsolvencyItem,
    updateInsolvencyItem,
    deleteInsolvencyItems,
  } = useCreditContext();

  const { errors, warnings } = credit;

  const { insolvencyRecords } = credit.insolvencyRecords;

  const baseUpdate = (insolvency: Insolvency, values: any, disableIncrementUpdateCount: boolean = false) => updateInsolvencyItem(
    values,
    insolvency.id,
    disableIncrementUpdateCount,
  );

  const update = (insolvency: Insolvency) => (values: any, disableIncrementUpdateCount: boolean = false) => baseUpdate(
    insolvency,
    values,
    disableIncrementUpdateCount,
  );

  const InsolvencyConsolidation = (insolvency: Insolvency) => useMemo(
    () => {
      if (insolvency.ignored) {
        return insolvency.isEquifax ? <IgnoredTag data={insolvency} /> : null;
      }

      if (checkInsolvencyIsSettled(insolvency)) {
        return insolvency.isEquifax ? <SettledItem /> : null;
      }

      const disabled = !insolvency.typeOfJudgement
        || ['Bankruptcy', 'IVA', 'DAS'].includes(insolvency.typeOfJudgement)
        || insolvency.registeredAtLandRegistry;

      const isLive = insolvency.isEquifax ? !checkInsolvencyIsSettled(insolvency) : !insolvency.satisfiedDate;

      return (
        <>
          {insolvency?.ignored && <IgnoredItem />}
          {!insolvency?.ignored && (
            <ModelForm model={insolvency} update={update(insolvency)} errors={errors} warnings={warnings}>
              <ModelSelect
                id={insolvency.id}
                field="consolidationType"
                placeholder="Please select"
                options={{
                  'NoConsolidation': ConsolidationType.NoConsolidation,
                  'Full': ConsolidationType.Full,
                }}
                disabled={!isLive || disabled}
                isTooltipError
              />
            </ModelForm>
          )}
        </>
      );
    },
    [insolvencyRecords],
  );

  const LandRegistryToggle = (insolvency: Insolvency) => useMemo(
    () => {
      if (insolvency.ignored || checkInsolvencyIsSettled(insolvency)) {
        return null;
      }
      return (
        <ModelForm model={insolvency} update={update(insolvency)} errors={errors} warnings={warnings}>
          <ModelTripleToggle
            id={`${insolvency.id}-isUnderDMP`}
            field="registeredAtLandRegistry"
            disabled={insolvency.typeOfJudgement === 'DAS'}
            containerStyles="width: auto;"
            isTooltipError
          />
        </ModelForm>
      );
    },
    [insolvencyRecords],
  );

  const InsolvencyValueOfJudgement = (insolvency: Insolvency) => useMemo(
    () => (
      <ModelForm model={insolvency} update={update(insolvency)} errors={errors} warnings={warnings}>
        <ModelNewNumericInput
          id={`${insolvency.id}-value`}
          field={insolvency.isEquifax ? 'value' : 'manualValue'}
          placeholder={insolvency.isEquifax ? '-' : ''}
          disabled={insolvency.isEquifax}
          isFixedDecimal
          isTooltipError
        />
      </ModelForm>
    ),
    [insolvencyRecords],
  );

  const InsolvencyTypeOfJudgement = (insolvency: Insolvency) => useMemo(
    () => (
      <ModelForm model={insolvency} update={update(insolvency)} errors={errors} warnings={warnings}>
        <ModelSelect
          id={insolvency.id}
          field="typeOfJudgement"
          placeholder="Please select"
          options={TypeOfJudgementType}
          disabled={insolvency.isEquifax}
          showValueTooltip="onHover"
          isTooltipError
        />
      </ModelForm>
    ),
    [insolvencyRecords],
  );

  const columns = [
    {
      name: 'Status',
      sortable: false,
      cell: (insolvency: Insolvency) => StatusIcon(insolvency),
      width: '60px',
    },
    {
      name: 'Applicant',
      sortable: false,
      minWidth: '260px',
      cell: (insolvency: Insolvency) => ApplicantName(
        insolvency?.id,
        insolvency?.isEquifax,
        insolvency?.ignored,
        insolvency?.nameMatchedData,
        credit?.applicants,
        insolvency,
        update(insolvency),
      ),
    },
    {
      name: 'Type of judgement',
      sortable: false,
      cell: (insolvency: Insolvency) => InsolvencyTypeOfJudgement(insolvency),
    },
    {
      name: 'Court date',
      sortable: false,
      width: '140px',
      cell: (insolvency: Insolvency) => InsolvencyCourtDate(insolvency, baseUpdate, errors, warnings),
    },
    {
      name: 'Satisfied date',
      sortable: false,
      width: '140px',
      cell: (insolvency: Insolvency) => InsolvencySatisfiedDate(insolvency, baseUpdate, errors, warnings),
    },
    {
      name: 'Value of debt (£)',
      sortable: false,
      minWidth: '120px',
      maxWidth: '200px',
      cell: (insolvency: Insolvency) => InsolvencyValueOfJudgement(insolvency),
    },
    {
      name: 'Consol',
      sortable: false,
      cell: (insolvency: Insolvency) => InsolvencyConsolidation(insolvency),
    },
    {
      name: 'Registered at Land Registry',
      sortable: false,
      cell: (insolvency: Insolvency) => LandRegistryToggle(insolvency),
    },
    {
      cell: (insolvency: Insolvency) => InsolvencyOptions(insolvency, deleteInsolvencyItems, update(insolvency)),
      sortable: false,
      width: '35px',
    },
  ];

  const addressId = address?.id;

  const data = Object
    .values(insolvencyRecords)
    .filter((insolvency: Insolvency) => insolvency.addressId === addressId)
    .sort((a, b) => a.ordinal - b.ordinal);

  const hasItems = !!data.length;

  const insolvencyRefName = `insolvency-credit-address-${addressId}`;
  const ref = useCreateAndAddRefs(insolvencyRefName);

  return (
    <TableContainer key={addressId}>
      <div id={insolvencyRefName} ref={ref} />

      <CreditTableAddress
        address={address}
        onClickAddCreditItem={() => addInsolvencyItem({
          addressId,
        }, insolvencyRefName)}
        hasItems={hasItems}
        id={insolvencyRefName}
      />

      {hasItems && (
        <CreditTable
          id="insolvency-table"
          columns={columns}
          data={data}
          expandableRowsComponent={InsolvencyDetails}
          expandableRowsComponentProps={{
            errors,
            warnings,
          }}
          showDisabledExpandableIcon
          expandableRowDisabled={(insolvency) => (
            !insolvency.isEquifax
            && (
              insolvency.typeOfJudgement == null
              || insolvency.consolidationType == null
              || !!insolvency.satisfiedDate
            )
          )}
          showSearch={false}
        />
      )}
    </TableContainer>
  );
}
